"use client";
/**
 * HoverTip — one global custom tooltip for the whole app.
 *
 * Native HTML `title` tooltips are unreliable in the Electron shell (often
 * suppressed or very delayed), so this component (mounted once in the root
 * layout) intercepts `title` and `data-tip` via event delegation and renders
 * a styled chip near the cursor. `title` is stolen while hovered so the
 * browser balloon never stacks on top.
 *
 * Optional `data-tip-side="top|bottom"` hints placement (default: below the
 * cursor, flipping up near the viewport bottom). `data-no-tip` opts out.
 */
import * as React from "react";
import { createPortal } from "react-dom";

const SHOW_DELAY_MS = 90;

function tipHost(start: EventTarget | null): Element | null {
  if (!(start instanceof Element)) return null;
  const el = start.closest("[data-tip], [data-tip-stolen], [title]");
  if (!el || el.closest("[data-no-tip]")) return null;
  if (el.closest("[data-hover-tip]")) return null;
  return el;
}

function tipText(el: Element): string {
  return (
    el.getAttribute("data-tip") ||
    el.getAttribute("data-tip-stolen") ||
    el.getAttribute("title") ||
    ""
  ).trim();
}

function stealTitle(el: Element): void {
  const t = el.getAttribute("title");
  if (!t) return;
  el.setAttribute("data-tip-stolen", t);
  el.removeAttribute("title");
}

function restoreTitle(el: Element | null): void {
  if (!el) return;
  const t = el.getAttribute("data-tip-stolen");
  if (t == null) return;
  el.setAttribute("title", t);
  el.removeAttribute("data-tip-stolen");
}

function place(el: HTMLElement, x: number, y: number, side: string | null): void {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const r = el.getBoundingClientRect();
  const w = r.width || 160;
  const h = r.height || 32;
  let left = x + 14;
  let top = side === "top" ? y - h - 12 : y + 16;
  if (left + w > vw - 8) left = Math.max(8, x - w - 14);
  if (top + h > vh - 8) top = Math.max(8, y - h - 12);
  if (top < 8) top = 8;
  if (left < 8) left = 8;
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

export function HoverTip() {
  const [text, setText] = React.useState<string | null>(null);
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  const hostRef = React.useRef<Element | null>(null);
  const posRef = React.useRef({ x: 0, y: 0 });
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const showingRef = React.useRef(false);

  React.useEffect(() => {
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const hide = () => {
      clear();
      restoreTitle(hostRef.current);
      hostRef.current = null;
      showingRef.current = false;
      setText(null);
    };
    const applyPos = () => {
      const box = boxRef.current;
      const host = hostRef.current;
      if (!box || !host) return;
      place(box, posRef.current.x, posRef.current.y, host.getAttribute("data-tip-side"));
    };
    const onOver = (e: MouseEvent) => {
      // Coarse pointers have no hover; a tap would pin a tooltip over the control.
      if (window.matchMedia("(hover: none)").matches) return;
      const host = tipHost(e.target);
      if (!host) return;
      const next = tipText(host);
      if (!next) return;
      posRef.current = { x: e.clientX, y: e.clientY };
      if (hostRef.current && hostRef.current !== host) restoreTitle(hostRef.current);
      hostRef.current = host;
      stealTitle(host);
      clear();
      if (showingRef.current) {
        setText(next);
        return;
      }
      timerRef.current = setTimeout(() => {
        if (hostRef.current !== host) return;
        showingRef.current = true;
        setText(next);
      }, SHOW_DELAY_MS);
    };
    const onMove = (e: MouseEvent) => {
      posRef.current = { x: e.clientX, y: e.clientY };
      if (boxRef.current && hostRef.current) applyPos();
    };
    const onOut = (e: MouseEvent) => {
      const from = tipHost(e.target);
      const to = tipHost(e.relatedTarget);
      if (from && from !== to) hide();
    };
    document.addEventListener("mouseover", onOver, true);
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("mouseout", onOut, true);
    document.addEventListener("mousedown", hide, true);
    document.addEventListener("wheel", hide, true);
    document.addEventListener("keydown", hide, true);
    return () => {
      hide();
      document.removeEventListener("mouseover", onOver, true);
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("mouseout", onOut, true);
      document.removeEventListener("mousedown", hide, true);
      document.removeEventListener("wheel", hide, true);
      document.removeEventListener("keydown", hide, true);
    };
  }, []);

  React.useLayoutEffect(() => {
    const box = boxRef.current;
    const host = hostRef.current;
    if (!box || !host || !text) return;
    place(box, posRef.current.x, posRef.current.y, host.getAttribute("data-tip-side"));
  }, [text]);

  if (!text || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={boxRef}
      data-hover-tip=""
      className="pointer-events-none fixed z-[2000] w-max max-w-[min(20rem,calc(100vw-16px))] rounded-md border border-line bg-surface/95 px-2 py-1 text-2xs leading-snug text-ink shadow-panel-raised backdrop-blur"
      style={{ left: 0, top: 0 }}
      role="tooltip"
    >
      {text}
    </div>,
    document.body,
  );
}
